import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useAuth } from '../../contexts/AuthContext';
import { Report } from '../../types/reports';
import { ReportModal } from './ReportModal';
import { DashboardLayout } from '../layout/DashboardLayout';

export function ReportsPage() {
  const supabase = useSupabaseClient();
  const { getEffectiveUserId, loading: authLoading } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const loadReports = useCallback(async () => {
    // Wait for auth to finish loading before fetching reports
    if (authLoading) return;
    
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let query = supabase
        .from('reports')
        .select('*')
        .eq('user_id', effectiveUserId)
        .order('created_at', { ascending: false });

      // Add search filter if search term exists
      if (searchTerm.trim()) {
        query = query.or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`);
      }

      const { data: reportsData, error } = await query;

      if (error) {
        throw error;
      }

      setReports(reportsData || []);
    } catch (err) {
      console.error('Error loading reports:', err);
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [supabase, getEffectiveUserId, searchTerm, authLoading]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleViewReport = (report: Report) => {
    setSelectedReport(report);
    setOpenModal(true);
  };

  const handleDeleteReport = (reportId: string) => {
    // Remove the deleted report from the local state
    setReports(prev => prev.filter(report => report.id !== reportId));
  };

  const getReportTypeColor = (type: string) => {
    switch (type) {
      case 'ask_quant':
        return 'primary';
      case 'analysis':
        return 'secondary';
      case 'summary':
        return 'success';
      default:
        return 'default';
    }
  };

  const getReportTypeLabel = (type: string) => {
    switch (type) {
      case 'ask_quant':
        return 'Ask Quant';
      case 'analysis':
        return 'Analysis';
      case 'summary':
        return 'Summary';
      default:
        return type;
    }
  };

  const truncateContent = (content: string, maxLength: number = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <DashboardLayout title="Reports">
      <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 }, backgroundColor: 'background.default' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AssessmentIcon sx={{ color: 'primary.main', mr: 1, fontSize: 32 }} />
            <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
              Reports
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
            View and manage all your generated reports and analyses.
          </Typography>

          {/* Search */}
          <TextField
            fullWidth
            placeholder="Search reports by title or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 400 }}
          />
        </Box>

        {/* Reports List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : reports.length === 0 ? (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
              No reports found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {searchTerm 
                ? 'Try adjusting your search terms.' 
                : 'Ask a question in the Ask Quant section to generate your first report!'
              }
            </Typography>
          </Paper>
        ) : (
          <List sx={{ p: 0 }}>
            {reports.map((report, idx) => (
              <React.Fragment key={report.id}>
                <ListItem sx={{ 
                  p: 0,
                  mb: 2
                }}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      width: '100%',
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      justifyContent: 'space-between',
                      mb: 2
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: 'text.primary', 
                            fontWeight: 600,
                            mb: 1
                          }}
                        >
                          {report.title}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            label={getReportTypeLabel(report.report_type)}
                            size="small"
                            color={getReportTypeColor(report.report_type) as any}
                          />
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {new Date(report.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewReport(report)}
                        sx={{ ml: 2 }}
                      >
                        View Report
                      </Button>
                    </Box>

                    {/* Content Preview */}
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        backgroundColor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 2
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.primary',
                          lineHeight: 1.5
                        }}
                      >
                        {truncateContent(report.content)}
                      </Typography>
                    </Paper>

                    {/* Metadata */}
                    {report.metadata && (
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {report.metadata.question && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            <strong>Question:</strong> {report.metadata.question}
                          </Typography>
                        )}
                        {report.metadata.search_results && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            <strong>Sources:</strong> {report.metadata.search_results.newsArticles} news, {report.metadata.search_results.exaResults} web
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Paper>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      <ReportModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedReport(null);
        }}
        report={selectedReport}
        onDelete={handleDeleteReport}
      />
    </DashboardLayout>
  );
}
